import {
  type ComponentType,
  type HTMLAttributeAnchorTarget,
  type MouseEvent,
  type MouseEventHandler,
  type Ref,
  useState,
} from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './NavigationLink.module.scss';

interface NavigationLinkProps {
  action?: 'logout';
  align?: 'left' | 'right';
  className?: string;
  /** Render as a child of a collapsible group (smaller, indented, brand-tint active). */
  isSub?: boolean;
  hasSubmenu?: boolean;
  highlight?: boolean;
  href: string;
  Icon?: ComponentType<{
    className?: string;
  }>;
  /** Trailing icon rendered after the label (e.g. chevron-right in mobile sheet rows). */
  TrailingIcon?: ComponentType<{
    className?: string;
  }>;
  label: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  ref?: Ref<HTMLAnchorElement>;
  refreshOnSameClick?: boolean;
  target?: HTMLAttributeAnchorTarget;
}

const NavigationLink = ({
  action,
  align = 'left',
  className,
  isSub = false,
  hasSubmenu = false,
  highlight = false,
  href,
  Icon,
  TrailingIcon,
  label,
  onClick,
  ref,
  refreshOnSameClick = false,
  target,
  ...rest
}: NavigationLinkProps) => {
  const { i18n, t } = useTranslation(['common']);
  const [refreshLinkToken, setRefreshLinkToken] = useState<number>(123);
  const lang = i18n.language ?? 'en';

  const correctedHref = href
    .replace('{lang}', lang === 'en' ? '' : lang)
    .replace(/([^:]\/)\/+/g, '$1'); // In some cases "//" is when the language shouldn't be part of the URL

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (action === 'logout') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    if (refreshOnSameClick) {
      setRefreshLinkToken(Date.now());
    }
    onClick?.(event);
  };

  return (
    <NavLink
      aria-label={t(label)}
      ref={ref}
      to={correctedHref}
      end={hasSubmenu}
      onClick={handleClick}
      target={target}
      className={({ isActive }) =>
        classNames(
          styles.link,
          {
            [styles.active]: isActive,
            [styles.alignRight]: align === 'right',
            [styles.sub]: isSub,
            [styles.highlight]: highlight,
          },
          className
        )
      }
      state={
        refreshLinkToken ? { refreshOnSameClick: refreshLinkToken } : undefined
      }
      {...rest}
    >
      {Icon && (
        <span className={styles.iconWrap}>
          <Icon className={styles.icon} />
        </span>
      )}
      <span className={styles.label}>{t(label)}</span>
      {TrailingIcon && (
        <span className={styles.trailingIconWrap}>
          <TrailingIcon className={styles.trailingIcon} />
        </span>
      )}
    </NavLink>
  );
};

export default NavigationLink;
